import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, unref as _unref, renderList as _renderList, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-450" }
const _hoisted_2 = {
  key: 0,
  class: "col gap-15 mleft-08 pad-m mtop-02"
}
const _hoisted_3 = {
  key: 0,
  class: "w-full"
}
const _hoisted_4 = {
  key: 1,
  class: "w-full"
}
const _hoisted_5 = {
  key: 2,
  class: "w-full"
}
const _hoisted_6 = {
  key: 1,
  class: "pad-s"
}
const _hoisted_7 = {
  key: 1,
  class: "mtop-20"
}

import patientInfoBox from '@/views/patientMain/patientInfoBox.vue';
import addMedicLine from './addMedicLine.vue';
import editMedicDoseUpper from './editMedicDoseUpper.vue';
import editMedicDoseLower from './editMedicDoseLower.vue';
import refSheetGroup from '../combinedReference/refSheetGroup.vue';
import { useAppUserStore } from '@/stores/appUser';
import { emptyParsedDose } from '@/functions/dosageUtils';
import gen from "@/functions/general.js";
import { normalizeString, sortMedicRefList } from "@/functions/medicReference";
import { useReferenceStore } from '@/stores/reference';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { useI18n } from "vue-i18n";
import { ref, computed, onMounted } from 'vue'
import { getSingleElement } from '@/utils/array-utils'
import { useRoute } from 'vue-router'

const maxItems = 8


export default /*@__PURE__*/_defineComponent({
  __name: 'addMedic',
  props: {
  showInfoBox: {
    type: Boolean,
    default: true,
  },  
  autoFocus: {
    type: Boolean,
    default: false,
  },
  suggestionsByExpr: {
    type: Object,
    default: null,
  },
  suggestionsFromCclText: {
    type: Object,
    default: null,
  },  
  //conclusionId = very important for saving data to the correct ccl
  conclusionId: {
    type: String,
    default: '',
  },
  resizeCombo: {
    type: Boolean,
    default: false
  },
  treatmentAdded: {
    type: Function,
    required: false
  }
},
  emits: ['refreshTreatment'],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const referenceStore = useReferenceStore();
const currentEntryStore = useCurrentEntryStore();
const appUserStore = useAppUserStore();
const searchInputRef = ref(null)
const searchInput = ref("")

const props = __props

const route = useRoute();

const dropd = ref("");
const emits = __emit

const filteredRefSheetsForResults = computed(() => {
  if (!props.suggestionsByExpr) return []

  const seen = new Set()
  const isNew = (medic) => {
    const key = medic.sctSubstanceKey || medic.name?.toLowerCase() || medic.originalText?.toLowerCase()
    if (seen.has(key)) {
      return false;
    } else {
      seen.add(key);
      return true;
    }
  }
  return props.suggestionsByExpr
    .map(refSheet => ({ ...refSheet, sctMedication: refSheet.sctMedication.filter(isNew) }))
    .filter(refSheet => refSheet.sctMedication.length > 0)
})

const filteredSuggestionsFromCclText = computed(() => {
  if (!props.suggestionsFromCclText) return []
  return props.suggestionsFromCclText
    .map(exprGroup => exprGroup.elements[0])
    .filter(expr => useAsMedicSuggestion(expr))
})

function useAsMedicSuggestion(expr) {
  if (expr.aggressorRootGroup === "Medic") return true;
  if (expr.isMatched) return false;
  const suggestions = appUserStore.currentAppUserData?.preferences.medicationSuggestions || [];
  return suggestions.some(item => item.originalText.toUpperCase() === expr.originalText.toUpperCase());
}

function closeFct() {
  searchInput.value = ''
  dropd.value = ''
  innerFocus()
}

async function closeAndShowFct() {
  searchInput.value = '';

  dropd.value = "EditDosage";
}

async function createCustomMedic(newSapName: string) {
  newSapName = gen.capitalize(newSapName)
  const conclusionId = props.conclusionId
  const doses = [emptyParsedDose()];
  const newSapExpr = await currentEntryStore.createNewSapExpr(conclusionId, newSapName, '', '', doses, null)

  currentEntryStore.setPoppedSapExpr(newSapExpr);
  currentEntryStore.reloadAfterMedicationChange();
  closeAndShowFct();
}

const newMedicTerm = computed(() => {
  let ret = searchInput.value
  return (ret.length > 1) ? ret.charAt(0).toUpperCase() + ret.slice(1) : ret.charAt(0).toUpperCase()
})

const isSamedMedic = computed(() => {
  return referenceStore.medicLuFullList.some(medic => medic.searchTerm === normalizedSearchString.value)
})

const isExstingMedic = computed(() => {
  return referenceStore.existingMedicList.some(medic => normalizeString(medic.matchedText) === normalizedSearchString.value)
})

const normalizedSearchString = computed(() => {
  return normalizeString(searchInput.value)
})

const filteredMultiSubstanceList = computed(() => {
  let ret = []
  let searchString = normalizedSearchString.value
  if (searchString.length < 2) return ret;
  const searchParts = searchString.split(" ")

  const matchesForEachPart = searchParts.map(part =>
    referenceStore.substanceFullList
      .filter(medic => medic.searchTerm.startsWith(part))
      .map(medic => medic.sctSubstanceKey.replace(/-----/g, ""))
  )

  ret = referenceStore.multiSubstanceList.filter(multiSubstance =>
    matchesForEachPart.every(matches =>
      matches.some(match => multiSubstance.substances.includes(match))
    )
  )
  sortMedicRefList(ret)
  return ret.slice(0, maxItems)
})

const filteredSubstanceList = computed(() => {
  let ret = []
  const searchString = normalizedSearchString.value
  if (searchString.length < 1) return ret
  const searchParts = searchString.split(" ")
  const firstPart = searchParts[0]
  const otherParts = searchParts.slice(1)

  if (searchParts.length === 1) {
    const startsWithList = referenceStore.substanceFullList.filter(medic => medic.searchTerm.startsWith(firstPart))
    sortMedicRefList(startsWithList)
    const includesList = []
    referenceStore.substanceFullList.forEach(medic => {
      if (medic.searchTerm.includes(firstPart) && !startsWithList.includes(medic)) includesList.push(medic);
    })
    sortMedicRefList(includesList)
    ret = [...startsWithList, ...includesList]
  } else if (searchParts.length > 1) {
    ret = referenceStore.substanceFullList.filter(medic => 
      medic.searchTerm.startsWith(firstPart) && otherParts.every(part => medic.searchTerm.includes(part))
    )
    sortMedicRefList(ret)
  }
  return ret.slice(0, maxItems)
})

const normalizeStr = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

const filteredMedicList = computed(() => {
  let ret = []
  const searchString = normalizedSearchString.value
  if (searchString.length < 1) return ret
  const searchParts = searchString.split(" ")
  const firstPart = searchParts[0]
  const otherParts = searchParts.slice(1)
  const firstPartNormalized = normalizeStr(firstPart);

  if (searchParts.length === 1) {
    //Existing Medics first
    const exactMatchList = referenceStore.existingMedicList.filter(expr => expr.matchedText.toUpperCase() === firstPart)
    const exactMatchTerms = new Set(exactMatchList.map(exact => exact.matchedText.toUpperCase()));
    //Matching lists
    const startsWithList = [];
    const includesList = [];
    referenceStore.medicLuFullList.forEach(medic => {
      const normalizedSearchTerm = normalizeStr(medic.searchTerm);
      if (exactMatchTerms.has(normalizedSearchTerm)) return; // skip if already in exact matches
      if (normalizedSearchTerm.startsWith(firstPartNormalized)) {
        startsWithList.push(medic);
      } else if (normalizedSearchTerm.includes(firstPartNormalized)) {
        includesList.push(medic);
      }
    });
    //Matching suggestions
    const suggestions = appUserStore.currentAppUserData?.preferences.medicationSuggestions || [];
    const matchingSuggestions = suggestions.filter(suggestion => 
      !suggestion.sctSubstanceKey && 
      !exactMatchTerms.has(normalizeStr(suggestion.originalText)) &&
      normalizeStr(suggestion.originalText).startsWith(firstPartNormalized)
    );
    //Sort and add to the return
    sortMedicRefList(startsWithList);
    sortMedicRefList(includesList);
    ret = [...exactMatchList, ...matchingSuggestions, ...startsWithList, ...includesList]
  } else if (searchParts.length > 1) {
    ret = referenceStore.medicLuFullList.filter(medic => 
      medic.searchTerm.startsWith(firstPart) && otherParts.every(part => medic.searchTerm.includes(part))
    )
    sortMedicRefList(ret)
  }
  return ret.slice(0, maxItems)
})

function innerFocus() {
  setTimeout(() => {
    searchInputRef.value.innerRef.focus()
  }, 1)
}


async function reloadMedicSearchReferences() {
  if (!currentEntryStore.hasCurrentEntry) return;
  const allExpressions = currentEntryStore.patientHistoryResponse;
  referenceStore.createMedicSearchReferences(allExpressions, true, getSingleElement(route.params.patientId)) 
}

onMounted(async () => {
  reloadMedicSearchReferences()
  if (props.autoFocus === true) {
    innerFocus()
  }
})


return (_ctx: any,_cache: any) => {
  const _component_dropdownBox = _resolveComponent("dropdownBox")!
  const _component_text_item = _resolveComponent("text-item")!
  const _component_listGroup = _resolveComponent("listGroup")!
  const _component_inputComboBox2 = _resolveComponent("inputComboBox2")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (props.showInfoBox)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(patientInfoBox, { info: "medic" }),
          _cache[14] || (_cache[14] = _createElementVNode("div", { class: "mtop-20" }, null, -1))
        ], 64))
      : _createCommentVNode("", true),
    _createVNode(_component_dropdownBox, {
      combo: true,
      modelValue: dropd.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dropd).value = $event)),
      openOn: "EditDosage"
    }, {
      dropup: _withCtx(() => [
        _createVNode(editMedicDoseUpper)
      ]),
      dropd: _withCtx(() => [
        _createVNode(editMedicDoseLower, {
          onTreatmentUpdated: __props.treatmentAdded,
          conclusionId: props.conclusionId,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (closeFct()))
        }, null, 8, ["onTreatmentUpdated", "conclusionId"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_inputComboBox2, {
      ref_key: "searchInputRef",
      ref: searchInputRef,
      placeholder: _unref(t)('patient.addMedication.placeholder'),
      modelValue: searchInput.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((searchInput).value = $event)),
      class: _normalizeClass({'combo-width': __props.resizeCombo})
    }, {
      dropd: _withCtx(() => [
        (searchInput.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              (filteredMedicList.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(_component_text_item, {
                      txt: _unref(t)('patient.addMedication.commercialNames'),
                      styles: "main-l font-mBold"
                    }, null, 8, ["txt"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredMedicList.value, (medic, index) => {
                      return (_openBlock(), _createBlock(addMedicLine, {
                        key: index,
                        content: medic,
                        conclusionId: props.conclusionId,
                        treatmentAdded: __props.treatmentAdded,
                        onClose: _cache[2] || (_cache[2] = ($event: any) => (closeFct())),
                        onCloseAndShow: _cache[3] || (_cache[3] = ($event: any) => (closeAndShowFct()))
                      }, null, 8, ["content", "conclusionId", "treatmentAdded"]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              (filteredSubstanceList.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(_component_text_item, {
                      txt: _unref(t)('patient.addMedication.singleSubstances'),
                      styles: "main-l font-mBold",
                      style: {"margin-bottom":"0.2rem"}
                    }, null, 8, ["txt"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSubstanceList.value, (medic, index) => {
                      return (_openBlock(), _createBlock(addMedicLine, {
                        key: index,
                        content: medic,
                        conclusionId: props.conclusionId,
                        treatmentAdded: __props.treatmentAdded,
                        onClose: _cache[4] || (_cache[4] = ($event: any) => (closeFct())),
                        onCloseAndShow: _cache[5] || (_cache[5] = ($event: any) => (closeAndShowFct()))
                      }, null, 8, ["content", "conclusionId", "treatmentAdded"]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              (filteredMultiSubstanceList.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(_component_text_item, {
                      txt: _unref(t)('patient.addMedication.multipleSubstances'),
                      styles: "main-l font-mBold"
                    }, null, 8, ["txt"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredMultiSubstanceList.value, (medic, index) => {
                      return (_openBlock(), _createBlock(addMedicLine, {
                        key: index,
                        content: medic,
                        conclusionId: props.conclusionId,
                        treatmentAdded: __props.treatmentAdded,
                        onClose: _cache[6] || (_cache[6] = ($event: any) => (closeFct())),
                        onCloseAndShow: _cache[7] || (_cache[7] = ($event: any) => (closeAndShowFct()))
                      }, null, 8, ["content", "conclusionId", "treatmentAdded"]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              (normalizedSearchString.value && !isSamedMedic.value && !isExstingMedic.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 3,
                    class: "w-full",
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (createCustomMedic(newMedicTerm.value)))
                  }, [
                    _createVNode(_component_text_item, {
                      txt: _unref(t)('action.create'),
                      styles: "main-l font-mBold"
                    }, null, 8, ["txt"]),
                    _createVNode(_component_listGroup, { styles: "pad-s" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_text_item, {
                          txt: newMedicTerm.value,
                          style: {"max-width":"15rem"}
                        }, null, 8, ["txt"])
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_component_text_item, {
                class: "mleft-06",
                txt: _unref(t)('patient.addMedication.placeholder'),
                styles: "main-m"
              }, null, 8, ["txt"])
            ]))
      ]),
      _: 1
    }, 8, ["placeholder", "modelValue", "class"]),
    (props.autoFocus)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7))
      : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredRefSheetsForResults.value, (refSheet, refSheetIndex) => {
      return (_openBlock(), _createBlock(refSheetGroup, {
        key: refSheetIndex,
        class: "mtop-10 w-450",
        refSheet: refSheet,
        resultReferenceType: "Treatment",
        showType: false
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(refSheet.sctMedication, (suggExpr, suggIndex) => {
            return (_openBlock(), _createBlock(addMedicLine, {
              key: suggIndex,
              treatmentAdded: __props.treatmentAdded,
              style: {"margin-left":"-0.2rem"},
              content: suggExpr,
              conclusionId: props.conclusionId,
              onClose: _cache[10] || (_cache[10] = ($event: any) => (closeFct())),
              onCloseAndShow: _cache[11] || (_cache[11] = ($event: any) => (closeAndShowFct()))
            }, null, 8, ["treatmentAdded", "content", "conclusionId"]))
          }), 128))
        ]),
        _: 2
      }, 1032, ["refSheet"]))
    }), 128)),
    (filteredSuggestionsFromCclText.value.length)
      ? (_openBlock(), _createBlock(refSheetGroup, {
          key: 2,
          class: "mtop-10"
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSuggestionsFromCclText.value, (expr, index) => {
              return (_openBlock(), _createBlock(addMedicLine, {
                key: index,
                treatmentAdded: __props.treatmentAdded,
                content: expr,
                conclusionId: props.conclusionId,
                onClose: _cache[12] || (_cache[12] = ($event: any) => (closeFct())),
                onCloseAndShow: _cache[13] || (_cache[13] = ($event: any) => (closeAndShowFct())),
                style: {"margin-left":"-0.2rem"}
              }, null, 8, ["treatmentAdded", "content", "conclusionId"]))
            }), 128))
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})